import { Injectable } from '@angular/core';
import { Auth, updatePassword } from '@angular/fire/auth';
import { AppService} from './app.service';
import { HttpClient } from '@angular/common/http';
import { User, IUserOrganization } from '../models/user.model';
import { UserGoal, UserHabit, UserHabitInstance } from '../models';
import { HelperService } from './helper.service';
import { Subject, Subscription } from 'rxjs';
import moment from 'src/app/utils/moment';
import { DatabaseService } from './database.service';
import { equalTo, limitToLast, orderByChild, startAt } from '@angular/fire/database';
import { StorageService } from './storage.service';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

export interface IFoodPreferences{
  [foodId: string]: "available" | "unavailable";
}

@Injectable({
  providedIn: 'root'
})

export class UserService {
  public userSubscription: Subscription;
  public user: User;
  public userSubject = new Subject<User>();
  
  constructor(
    private appService: AppService,
    private dbService: DatabaseService,
    private firebaseAuth: Auth,
    private helperService: HelperService,
    private http: HttpClient,
    private storageService: StorageService,
  ) { }


  // User
  async updatePassword(password): Promise<void> { 
    const user = this.firebaseAuth.currentUser;
    if (!user) {
      throw new Error('Not authenticated');
    }
    return updatePassword(user, password)
  }

  async addProfilePicture(imageData: Blob): Promise<string> {
    const downloadURL = await this.storageService.fbPutFile(`users/${this.user.uid}/profilePicture`, imageData);
    await this.dbService.updateRef('/users/' + this.user.uid, {profilePicture: downloadURL})
    return downloadURL
  }
   
  // Workout Tracking  
  getTrackedWorkoutsList(userId: string, limit = 400) {
    return this.dbService.getListByRefTest(`/workouts/tracked/${userId}`, orderByChild("creationDate"), limitToLast(limit));
  }

  async getTrackedWorkoutAsPromise(userId: string, workoutId: string) {
    const snap = await this.dbService.getSnapshot(`/workouts/tracked/${userId}/${workoutId}`);
    return snap? {id: workoutId, ...snap} : null;
  }

  async getTrackedWorkoutsAsPromise(userId: string, programId?: string, limit = 400) {
    let snap;
    if(programId) {
      snap = await this.dbService.getSnapshot(`/workouts/tracked/${userId}`, orderByChild("workoutProgram"), equalTo(programId))
    } else {
      snap = await this.dbService.getSnapshot(`/workouts/tracked/${userId}`, orderByChild("creationDate"), limitToLast(limit))
    }
    return this.helperService.transformFirebaseObjectToArray(snap);
  }

  async getTrackedWorkoutByDate(userId: string, date: string): Promise<any> {
    const snap = await this.dbService.getSnapshot(`/workouts/tracked/${userId}`, orderByChild("date"), equalTo(date));
    return snap? this.helperService.transformFirebaseObjectToArray(snap)[0] : null;
  }

  async deleteTrackedWorkout(userId: string, workoutId: string) {
    return this.dbService.removeRef(`/workouts/tracked/${userId}/${workoutId}`);
  }

  async getLastTrackedWorkout(userId: string, workout) {
    let allTracked = await this.getTrackedWorkoutsAsPromise(userId, workout.workoutProgram);
    if(allTracked && allTracked.length > 0) {
      let workoutTracked = allTracked.filter((tracked) => {
        return tracked.workout === workout.workout && tracked.trackingCompleted && tracked.id !== workout.id
      }).sort((a, b) => {
        let dateA = new Date(a.trackingCompleted);
        let dateB = new Date(b.trackingCompleted);
        return dateA > dateB ? -1 : dateA < dateB ? 1 : 0;
      });
      if(workoutTracked.length > 0) {
        return workoutTracked[0]
      } else {
        return null
      }
    } else {
      return null
    }
  }

  // Meal Plans
  async getTrackedMealPlansAsPromise(userId: string) {
    const snap = await this.dbService.getSnapshot(`/nutritionTracking/${userId}`)
    return this.helperService.transformFirebaseObjectToArray(snap);
  }

  // Cardio
  async getTrackedCardioAsPromise(userId: string, programId?: string, limit = 400) {
    let snap;
    if(programId) {
      snap = await this.dbService.getSnapshot(`/cardioTracking/${userId}`, orderByChild("cardioProgram"), equalTo(programId))
    } else {
      snap = await this.dbService.getSnapshot(`/cardioTracking/${userId}`, orderByChild("creationDate"), limitToLast(limit))
    }
    return this.helperService.transformFirebaseObjectToArray(snap);
  }

  getTrackedCardioList(userId: string, limit = 400) {
    return this.dbService.getListByRefTest(`/cardioTracking/${userId}`, orderByChild("creationDate"), limitToLast(limit));
  }

  async addTrackCardio(userId: string, trackObj: any) {
    const ref = await this.dbService.pushByRef(`/cardioTracking/${userId}`, trackObj);
    return ref.key
  }

  updateTrackCardio(userId, id, trackObj) {
    return this.dbService.updateRef(`/cardioTracking/${userId}/${id}`, trackObj);
  }

  async deleteTrackedCardio(userId: string, workoutId: string) {
    return this.dbService.removeRef(`/cardioTracking/${userId}/${workoutId}`);
  }

  // Tracking
  updateTrackWorkout(userId, id, trackObj) {
    return this.dbService.updateRef(`/workouts/tracked/${userId}/${id}`, trackObj)
  }

  async addTrackWorkout(userId: string, trackObj: any) {
    const ref = await this.dbService.pushByRef(`/workouts/tracked/${userId}`, trackObj);
    return ref.key
  }

  activateUser(userId: string): Promise<User> {
    return new Promise((resolve, reject) => {
      this.userSubscription = this.dbService.getObjectByRef(`users/${userId}`, "uid").subscribe(snap => {
        if(snap) {
          const user = new User(snap);
          user.uid = userId;
          user.$id = user.uid;
          this.user = user;
          this.userSubject.next(this.user)
          resolve(this.user);
        } else {
          reject()
        }
      });
    });
  }

  clearUserSubscription() {
    if(this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  setAppUser(user: User) {
    this.user = user;
  }

  getAppUser() {
    return this.user || null;
  }

  verifyPasswordToken(token) {
    return this.http.get(`${this.appService.getApiURL()}/auth/verifyToken`, {
      headers: {
        Authorization: 'Bearer ' + token,
      }
    }).toPromise();
  }

  setPassword(password: string, token: string): Promise<any> {
    return this.http.post(`${this.appService.getApiURL()}/auth/setPassword`, {password: password}, {
      headers: {
        Authorization: 'Bearer ' + token,
      }
    }).toPromise();
  }

  deleteAccount(userId: string): Promise<any> {
    return this.http.delete(`${this.appService.getApiURL()}/users/${userId}`).toPromise();
  }

  reverseAccountDeletion(userId: string) {
    this.dbService.removeRef(`users/${userId}/deletionDate`);
  }

  // Service Functions
  addClient(orgName: string, userObj: any): Promise<Object> {
    let clientUrl = `${this.appService.getApiURL()}/organizations/${orgName}/clients`;
    return this.http.post(clientUrl, userObj).toPromise()
    .then(this.helperService.handlePromiseSuccess)
    .catch(this.helperService.handlePromiseRejection)
  }

  updateUser(userId: string, userObj: Partial<User>): Promise<Object> {
    let userUrl = `${this.appService.getApiURL()}/users/${userId}`;
    return this.http.put(userUrl, userObj).toPromise()
    .then(this.helperService.handlePromiseSuccess)
    .catch(this.helperService.handlePromiseRejection)
  }

  updateDBUser(userId: string, userObj: Partial<User>) {
    return this.dbService.updateRef(`users/${userId}`, userObj);
  }

  clearUserDeletion(userId: string) {
    return this.dbService.removeRef(`users/${userId}/deletionDate`);
  }

  setLastLogin(userId: string) {
    this.dbService.updateRef(`users/${userId}`, {lastLogin: (moment as any)().toISOString()});
    return this.storeLogin(userId, (moment as any)().format("YYYY-MM-DD"))
  }

  getLastLogin(userId: string) {
    return this.dbService.getSnapshot(`users/${userId}/lastLogin`)
  }

  setTimezone(userId: string) {
    this.dbService.updateRef(`users/${userId}`, {timezone: moment.tz.guess()});
    return this.storeLogin(userId, (moment as any)().format("YYYY-MM-DD"))
  }

  async updateDeviceInfo(userId: string) {
    try {
      let platform: string;
      let appVersion: string | null;

      // Detect platform
      if (Capacitor.isNativePlatform()) {
        platform = Capacitor.getPlatform(); // 'ios' or 'android'
        const appInfo = await App.getInfo();
        appVersion = appInfo.version;
      } else {
        platform = 'web';
        // For web, use package.json version or a default
        appVersion = null; // Update this to match your actual web version or use environment variable
      }

      const deviceInfo = {
        platform: platform,
        appVersion: appVersion,
        lastActive: new Date().toISOString()
      };

      // Update platform-specific device info (preserves other platforms)
      await this.dbService.setRef(`userSettings/${userId}/devices/${platform}`, deviceInfo);
      
      // Update last active device reference
      await this.dbService.setRef(`userSettings/${userId}/lastActiveDevice`, deviceInfo);

      return Promise.resolve();
    } catch (error) {
      console.error('Error updating device info:', error);
      // Don't throw - fail silently to not break app initialization
      return Promise.resolve();
    }
  }

  async getDeviceInfo(userId: string): Promise<{ platform: string, appVersion: string | null, lastActive: string } | null> {
    try {
      return await this.dbService.getSnapshot(`userSettings/${userId}/lastActiveDevice`);
    } catch (error) {
      console.error('Error getting device info:', error);
      return null;
    }
  }

  async getAllDeviceInfo(userId: string): Promise<{ web?: any, ios?: any, android?: any, lastActiveDevice?: any } | null> {
    try {
      const devices = await this.dbService.getSnapshot(`userSettings/${userId}/devices`);
      const lastActiveDevice = await this.dbService.getSnapshot(`userSettings/${userId}/lastActiveDevice`);
      
      return {
        ...devices,
        lastActiveDevice: lastActiveDevice
      };
    } catch (error) {
      console.error('Error getting all device info:', error);
      return null;
    }
  }

  storeLogin(userId: string, date: string) {;
    return this.dbService.setRef(`userActivities/${userId}/logins/${date}`, (moment as any)().toISOString());
  }
  

  getUser(userId) {
    return this.dbService.getObjectByRef(`users/${userId}`, "uid")
  }

  async getUserAsPromise(userId): Promise<User> {
    const userSnap = await this.dbService.getSnapshot(`users/${userId}`)
    let user = new User(userSnap);
    user.uid = userId;
    user.$id = user.uid;
    return user;
  } 

  async getUserByStripeId(stripeId: string) {
    const snap = await this.dbService.getSnapshot(`users`, orderByChild("stripeId"), equalTo(stripeId));
    return snap? this.helperService.transformFirebaseObjectToArray(snap).map((item) => {return new User(item)}) : snap;
  }

  async getUserEmail(userId): Promise<string> {
    return this.dbService.getSnapshot(`users/${userId}/email`);
  } 

  getUserSettings(userId: string, settingType: string) {
    return this.dbService.getSnapshot(`/userSettings/${userId}/${settingType}`)
  }

  updateUserSettings(userId: string, settingType: string, data: any) {
    return this.dbService.setRef(`/userSettings/${userId}/${settingType}`, data);
  }

  async addSavedMessage(userId: string, data: {title?: string, message: string}) {
    return this.dbService.pushByRef(`/userSettings/${userId}/savedMessages`, {
      title: data.title || null,
      message: data.message || null,
      creationDate: (moment as any)().toISOString(),
    })
  }

  async updateSavedMessage(userId: string, messageId: string,  data: {title?: string, message: string, order?: number}) {
    return this.dbService.updateRef(`/userSettings/${userId}/savedMessages/${messageId}`, {
      title: data.title || null,
      message: data.message || null,
      order:  this.helperService.valueExists(data.order)? data.order : null,
      lastModifiedDate: (moment as any)().toISOString(),
    });
  }

  deleteSavedMessage(userId: string, messageId: string) {
    this.dbService.removeRef(`/userSettings/${userId}/savedMessages/${messageId}/`);
  }

  async getUserProfileField(userId: string, profileField: string) {
    return this.dbService.getSnapshot(`users/${userId}/${profileField}`);
  }

  getUserOrganization(userId: string, orgId: string) {
    return this.dbService.getSnapshot(`users/${userId}/organizations/${orgId}`);
  }

  updateUserOrganization(userId: string, orgId: string, data: Partial<IUserOrganization>) {
    return this.dbService.updateRef(`users/${userId}/organizations/${orgId}`, data);
  }

  getAllUsersList() {
    return this.dbService.getListByRefTest(`/users`);
  }

  async getUserCreatedOnSince(isoDate: string): Promise<string[]> {
    const snap = await this.dbService.getSnapshot(
      'users',
      orderByChild('createdOn'),
      startAt(isoDate),
    );
    if (!snap) {
      return [];
    }
    return Object.values(snap)
      .map((user: any) => user?.createdOn)
      .filter((date): date is string => typeof date === 'string');
  }

  async getAllUsersAsPromise(): Promise<User[]>{
    const snap = await this.dbService.getSnapshot(`/users`);
    return snap? this.helperService.transformFirebaseObjectToArray(snap).map((item) => {return new User({uid: item.id, ...item})}) : null
  }

  getFoodPreferences(userId: string): Promise<any> {
    return this.dbService.getSnapshot(`/foodPreferences/${userId}`);
  }

  async updateFoodPreferences(userId: string, foodPreferences: {[foodId: string]: "available" | "unavailable"}) {
    return this.dbService.updateRef(`/foodPreferences/${userId}`, {
      ...foodPreferences,
      "*lastModified": (moment as any)().toISOString()
    });
  }

  async updateFoodPreference(userId: string, foodId: string, preference: "available" | "unavailable") {
    return this.dbService.setRef(`/foodPreferences/${userId}/${foodId}`, preference);
  }

  async updateExercisePreference(userId: string, exerciseId: string, preference: "like" | "dislike") {
    return this.dbService.setRef(`/exercisePreferences/${userId}/${exerciseId}`, preference);
  }

  getExercisePreference(userId: string, exerciseId: string): Promise<"like" | "dislike" | undefined> {
    return this.dbService.getSnapshot(`/exercisePreferences/${userId}/${exerciseId}`);
  }

  getEquipmentList(userId: string) {
    return this.dbService.getSnapshot(`/equipmentList/${userId}`);
  }

  async updateEquipmentList(userId: string, equipmentList: {[equipmentValue: string]: "available" | "unavailable"}) {
    return this.dbService.updateRef(`/equipmentList/${userId}`, {
      ...equipmentList,
      "*lastModified": (moment as any)().toISOString()
    })
  }

  addAction(userId: string, actionName: "foodPreferences" | "equipmentList") {
    return this.dbService.setRef(`/users/${userId}/actions/${actionName}`, (moment as any)().toISOString());
  }

  removeAction(userId: string, actionName: "foodPreferences" | "equipmentList") {
    return this.dbService.removeRef(`/users/${userId}/actions/${actionName}`);
  }

  removeUserOrganization(userId: string, organizationId: string) {
    return this.dbService.removeRef(`/users/${userId}/organizations/${organizationId}`);
  }

  sendInvitation(userId: string) {
    return this.http.post(`${this.appService.getApiURL()}/auth/invite`, {user: userId})
    .toPromise()
    .then(this.helperService.handlePromiseSuccess)
    .catch(this.helperService.handlePromiseRejection)
  }

  addTrainerToClient(userId: string, trainerId: string, role: "trainer" | "consultant", accountId: string) {
    return this.http.post(`${this.appService.getApiURL()}/clients/${userId}/access`, {id: trainerId, role: role, account: accountId})
    .toPromise()
    .then(this.helperService.handlePromiseSuccess)
    .catch(this.helperService.handlePromiseRejection)
  }

  removeTrainerFromClient(userId: string, trainerId: string) {
    return this.http.delete(`${this.appService.getApiURL()}/clients/${userId}/access/${trainerId}`)
    .toPromise()
    .then(this.helperService.handlePromiseSuccess)
    .catch(this.helperService.handlePromiseRejection)
  }

  // User Plans
  getActiveSubscriptions(userId: string) {
    return new Promise((resolve, reject) => {
      const url = `${this.appService.getApiURL()}/users/${userId}/subscriptions`;
      this.http.get(url, {}).toPromise()
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err.error ? err.error : err)
        });
    });
  }

  cancelSubscription(userId: string, subscriptionId: string) {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.appService.getApiURL()}/users/${userId}/subscriptions/cancel`, {cancel: true, subscriptionId: subscriptionId})
      .toPromise()
      .then(res => { resolve(res) })
      .catch(err => { reject(err) })
    });
  }

  getUserInvoices(userId: string) {
    return new Promise((resolve, reject) => {
      this.http.get(`${this.appService.getApiURL()}/users/${userId}/invoices`)
        .toPromise()
        .then((res) => resolve(res))
        .catch((err) => reject(err.error ? err.error : err));
    });
  }

  getUserPlatformPayments(userId: string) {
    return new Promise((resolve, reject) => {
      this.http.get(`${this.appService.getApiURL()}/users/${userId}/payments`)
        .toPromise()
        .then((res) => resolve(res))
        .catch((err) => reject(err.error ? err.error : err));
    });
  }

  refundUserInvoice(userId: string, invoiceId: string, amount?: number) {
    return new Promise((resolve, reject) => {
      this.http.put(`${this.appService.getApiURL()}/users/${userId}/invoices/${invoiceId}/refund`, {
        status: "refunded",
        invoiceId,
        amount,
      })
        .toPromise()
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  }

  // Public 
  getUserPublicAvailability(userId: string) {
    return new Promise((resolve, reject) => {
      const trainertUrl = `${this.appService.getApiURL()}/users/${userId}/public/availability`;
      this.http.get(trainertUrl, {}).toPromise()
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err.error ? err.error : err)
        });
    });
  }

  addGoal(userId: string, goal: Partial<UserGoal>) {
    return this.dbService.pushByRef(`/userGoals/${userId}`, {
      ...goal,
    });
  }

  updateGoal(userId: string, goalId: string, goal: Partial<UserGoal>) {
    return this.dbService.updateRef(`/userGoals/${userId}/${goalId}`, goal);
  }

  async getGoalAsPromise(userId: string, goalId: string): Promise<UserGoal> {
    const snap = await this.dbService.getSnapshot(`/userGoals/${userId}/${goalId}`);
    return snap? new UserGoal({id: goalId, ...snap}) : null;
  }

  async getGoalsAsPromise(userId: string): Promise<UserGoal[]> {
    const snap = await this.dbService.getSnapshot(`/userGoals/${userId}`);
    return snap? this.helperService.transformFirebaseObjectToArray(snap).map((item) => {return new UserGoal(item)}) : null;
  }

  getGoals(userId: string) {
    return this.dbService.getListByRefTest(`/userGoals/${userId}`);
  }

  removeGoal(userId: string, goalId: string) {
    return this.dbService.removeRef(`/userGoals/${userId}/${goalId}`);
  }

  // Habits
  addHabit(userId: string, habit: Partial<UserHabit>) {
    return this.dbService.pushByRef(`/userHabits/${userId}/definitions`, {
      ...habit,
    });
  }

  updateHabit(userId: string, habitId: string, habit: Partial<UserHabit>) {
    return this.dbService.updateRef(`/userHabits/${userId}/definitions/${habitId}`, habit);
  }

  async getHabitAsPromise(userId: string, goalId: string): Promise<UserHabit> {
    const snap = await this.dbService.getSnapshot(`/userHabits/${userId}/definitions/${goalId}`);
    return snap? new UserHabit({id: goalId, ...snap}) : null;
  }

  async getHabitsAsPromise(userId: string): Promise<UserHabit[]> {
    const snap = await this.dbService.getSnapshot(`/userHabits/${userId}/definitions`);
    return snap? this.helperService.transformFirebaseObjectToArray(snap).map((item) => {return new UserHabit(item)}) : null;
  }

  getHabits(userId: string) {
    return this.dbService.getListByRefTest(`/userHabits/${userId}/definitions`);
  }

  removeHabit(userId: string, habitId: string) {
    return this.dbService.removeRef(`/userHabits/${userId}/definitions/${habitId}`);
  }

  async getHabitInstanceAsPromise(userId: string, habitId: string, date: string): Promise<UserHabitInstance> {
    const snap = await this.dbService.getSnapshot(`/userHabits/${userId}/instances/${habitId}/${date}`);
    return snap? {id: date, ...snap} : null;
  }

  /** All dated instances for one habit (for Stats adherence rollups). */
  async getHabitInstancesAsPromise(userId: string, habitId: string): Promise<UserHabitInstance[]> {
    const snap = await this.dbService.getSnapshot(`/userHabits/${userId}/instances/${habitId}`);
    if (!snap) {
      return [];
    }
    return this.helperService.transformFirebaseObjectToArray(snap).map((item) => ({
      id: item.id,
      achieved: !!item.achieved,
      date: item.date || item.id,
      notes: item.notes,
    }));
  }

  addHabitInstance(userId: string, habitId: string, date: string, instance: Partial<UserHabitInstance>) {
    return this.dbService.updateRef(`/userHabits/${userId}/instances/${habitId}/${date}`, {
      ...instance,
      creationDate: (moment as any)().toISOString(),
      date: date,
    });
  }

  updateHabitInstance(userId: string, habitId: string, date: string, instance: Partial<UserHabitInstance>) {
    return this.dbService.updateRef(`/userHabits/${userId}/instances/${habitId}/${date}`, instance);
  }

  async getHabitsWithInstancesForDateAsPromise(userId: string, date: string, organizationId?: string): Promise<UserHabit[]> {
    const habitsWithInstances: UserHabit[] = [];
    const habits = await this.getHabitsAsPromise(userId);
    if(habits) {
      for(const habit of habits) {
        // Filter by status and ownership
        if(habit.status === "active" && (habit.createdBy === userId || habit.organization === organizationId)) {
          const instance = await this.getHabitInstanceAsPromise(userId, habit.id, date);
          if(instance) {
            habitsWithInstances.push(habit);
          }
        }
      }
    }
    return habitsWithInstances;
  }

  getUserActivity(userId: string, type: "logins") {
    return this.dbService.getSnapshot(`userActivities/${userId}/${type}`)
  }
}
